import { resource } from "@util/resource";
import { Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { ExperienceShortDescription } from "../../interfaces/dws/experience-short-description";
import {
  ExperienceDWS,
  ExperienceToImport,
} from "../../interfaces/dws/experience-dws";

export interface SlotOperationResult {
  slotKey: string;
  action: string;
  status: string;
  reason?: string;
}

export interface UpsertExperienceSlotsReport {
  experienceId?: string;
  eventType?: boolean;
  requestedSlots?: number;
  matchedSlots?: number;
  created?: number;
  updated?: number;
  deleted?: number;
  unchanged?: number;
  skipped?: number;
  failed?: number;
  warningsCount?: number;
  operationsTruncated?: boolean;
  operationLimit?: number;
  warnings?: string[];
  operations?: SlotOperationResult[];
}

export interface ExperienceSaveResponse {
  id: string;
  status?: string;
  message?: string;
  slotsReport?: UpsertExperienceSlotsReport;
  hasPropagableChanges?: boolean;
  showAsyncPropagationPopup?: boolean;
  propagableChangesSummary?: string[];
  /** Present when recurring slot propagation was queued as a background job */
  asyncJobId?: string | null;
}

/** Matches backend {@code WineSuiteJobsAsyncStatusResponse} */
export interface WineSuiteJobStatus {
  jobId: string;
  jobType: string;
  status: string;
  wineryId: string;
  entityType?: string;
  entityId?: string;
  /** Experience title from save payload; use for UI job label. */
  experienceName?: string | null;
  syncOk?: boolean | null;
  requestedAt?: string;
  startedAt?: string;
  completedAt?: string;
  errorSummary?: string;
  inputPayload?: Record<string, unknown>;
  resultPayload?: Record<string, unknown>;
  progress?: {
    totalTasks: number;
    pendingTasks: number;
    runningTasks: number;
    completedTasks: number;
    failedTasks: number;
  };
}

@Injectable({
  providedIn: "root",
})
export class ExperiencesService {
  constructor() {}

  getFromDWSbyUD(experienceId: string): Observable<ExperienceDWS> {
    return resource("crm://experiences/id").params({ id: experienceId }).get();
  }

  editExperienceShortDescription(
    experience: ExperienceShortDescription
  ): Observable<unknown> {
    return resource("crm://experiences/short-description").post(experience);
  }

  editSaveExperience(experience: ExperienceToImport): Observable<unknown> {
    return resource("crm://experiences").post(experience);
  }

  doSyncExperience(experienceId: string): Observable<String> {
    return resource("crm://v2/experiences/sync/id").params({ id: experienceId })
    .post();
  }
  doSyncExperienceAsync(experienceId: string): Observable<String> {
    return resource("crm://v2/experiences/sync/id").params({ id: experienceId })
    .post();
  }

  doSlotSyncExperience(experienceId: string): Observable<String> {
    return resource("crm://availabilities/admin/sync/id")
      .params({ id: experienceId })
      .post();
  }

  /** Full day-range widget sync (room peers included). Prefer over legacy slot sync. */
  doFullSlotSyncExperience(
    experienceId: string,
    body?: { dayStart?: string; dayEnd?: string; time?: string; date?: string; anchorOnly?: boolean }
  ): Observable<unknown> {
    return resource("crm://availabilities/admin/full-sync/id")
      .params({ id: experienceId })
      .post(body ?? {});
  }

  /** Full widget slot reconciliation for this experience only (super-admin; no room peers). */
  doFullSlotsOnlySyncExperience(experienceId: string): Observable<unknown> {
    return this.doFullSlotSyncExperience(experienceId, { anchorOnly: true });
  }

  doImportExperience(experienceJSON: any): Observable<String> {
    return resource("crm://experiences/import").post(experienceJSON);
  }

  doActiveExperience(
    experienceId: string,
    wineryId: string,
    active: boolean
  ): Observable<String> {
    return resource("crm://experiences/active").post({
      experienceId,
      wineryId,
      active,
    });
  }

  enableSlotAutomationsForExperience(data: {
    experienceId: string;
    wineryId: string;
    slotAutomationTypes: string[];
  }): Observable<unknown> {
    return resource("crm://experiences/experienceId/enabled-slot-automations")
      .params({ experienceId: data.experienceId })
      .post({
        wineryId: data.wineryId,
        slotAutomationTypes: data.slotAutomationTypes,
      });
  }

  listEnabledSlotAutomationsForExperience(
    experienceId: string
  ): Observable<{ slotAutomationTypes: string[] }> {
    return resource("crm://experiences/experienceId/enabled-slot-automations")
      .params({ experienceId })
      .get<{ slotAutomationTypes: string[] }>();
  }

  getExperiencesFromDWS(
    includeInactive: boolean = false
  ): Observable<ExperienceDWS[]> {
    return resource("crm://experiences/list")
      .params({ includeInactive: includeInactive })
      .get();
  }

  getExperiencesV2FromDWS(
    includeInactive: boolean = false
  ): Observable<ExperienceDWS[]> {
    return resource("crm://v2/experiences")
      .params({ includeInactive: includeInactive })
      .get();
  }

  getExperiencesV3FromDWS(
    includeInactive: boolean = false,
    lang: string = "IT"
  ): Observable<ExperienceDWS[]> {
    return resource("crm://v3/experiences")
      .params({ includeInactive, lang: lang.toUpperCase() })
      .get();
  }

  queryExperiencesV3FromDWS(filter: {
    includeInactive?: boolean;
    includeArchived?: boolean;
    fullTextSearch?: string;
    lang?: string;
  }): Observable<ExperienceDWS[]> {
    return resource("crm://v3/experiences/query").post({
      includeInactive: filter.includeInactive ?? false,
      includeArchived: filter.includeArchived ?? false,
      fullTextSearch: filter.fullTextSearch?.trim() || null,
      lang: (filter.lang || "IT").toUpperCase(),
    });
  }

  getExperienceV2FromDWS(id: string): Observable<ExperienceDWS> {
    return resource("crm://v2/experiences/id").params({ id: id }).get();
  }

  createUpdateExperience(
    experience: ExperienceDWS
  ): Observable<ExperienceSaveResponse> {
    return resource("crm://v2/experiences").post(experience);
  }

  previewPropagation(
    experience: ExperienceDWS
  ): Observable<UpsertExperienceSlotsReport> {
    return resource("crm://v2/experiences/preview-propagation").post(experience);
  }

  getWineSuiteJobStatus(jobId: string): Observable<WineSuiteJobStatus> {
    return resource("crm://v2/winesuite-jobs-async/jobId")
      .params({ jobId })
      .get<WineSuiteJobStatus>();
  }

  /** Raw CSV text for {@code GET /v2/winesuite-jobs-async/{jobId}/report.csv} */
  downloadWineSuiteJobReportCsvText(jobId: string): Observable<string> {
    return resource("crm://v2/winesuite-jobs-async/jobId/report-csv")
      .params({ jobId })
      .get("text");
  }

  /** Binary .xlsx for {@code GET /v2/winesuite-jobs-async/{jobId}/report.xlsx} */
  downloadWineSuiteJobReportXlsxBlob(jobId: string): Observable<Blob> {
    return resource("crm://v2/winesuite-jobs-async/jobId/report-xlsx")
      .params({ jobId })
      .get("blob");
  }
}
